import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { toast } from "sonner";
import { useLoggedMutation } from "@/hooks/use-action-log-session";
import type { useUndoRedoHistory } from "@/hooks/use-undo-redo-history";
import { notifyError } from "@/lib/notify-error";
import type { MeasurementOverlayRecord } from "@/viewport-tools/measurement/overlay";
import type { MeasurementHistoryLineage } from "./measurement-history-lineage";

interface LabelGroupingOptions {
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId: Id<"takeoffPilotPlanDocuments"> | null;
  activePage: number;
  pageMeasurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  history: Pick<ReturnType<typeof useUndoRedoHistory>, "isApplying" | "record">;
  enqueueHistoryEdit: <T>(operation: () => Promise<T>) => Promise<T>;
  measurementHistory: MeasurementHistoryLineage;
  getMeasurementMutationErrorMessage: (error: unknown, fallback: string) => string;
}

export function useMeasurementLabelGrouping({
  jobId,
  planDocumentId,
  activePage,
  pageMeasurements,
  selectedMeasurementIds,
  history,
  enqueueHistoryEdit,
  measurementHistory,
  getMeasurementMutationErrorMessage,
}: LabelGroupingOptions) {
  const groupMeasurementLabels = useLoggedMutation(api.takeoffPilot.measurements.groupLabels);
  const ungroupMeasurementLabels = useLoggedMutation(api.takeoffPilot.measurements.ungroupLabels);
  const groupAllMeasurementLabelsForTag = useLoggedMutation(
    api.takeoffPilot.measurements.groupAllLabelsForTag
  );
  const ungroupAllMeasurementLabelsForTag = useLoggedMutation(
    api.takeoffPilot.measurements.ungroupAllLabelsForTag
  );

  const restoreLabelGroupState = useLoggedMutation(api.takeoffPilot.measurements.restoreLabelGroupState);

  const recordGrouping = (result: Pick<Awaited<ReturnType<typeof groupMeasurementLabels>>, "history" | "measurementVersions">) => {
    const { history: change, measurementVersions } = result;
    const cursors = measurementHistory.track(measurementVersions);
    measurementHistory.advance(cursors, measurementVersions);
    if (change.before.length === 0) return;
    history.record({
      undo: async () => {
        const restored = await restoreLabelGroupState({ jobId, expected: change.after, target: change.before, expectedMeasurementVersions: measurementHistory.expected(cursors) });
        measurementHistory.advance(cursors, restored.measurementVersions);
      },
      redo: async () => {
        const restored = await restoreLabelGroupState({ jobId, expected: change.before, target: change.after, expectedMeasurementVersions: measurementHistory.expected(cursors) });
        measurementHistory.advance(cursors, restored.measurementVersions);
      },
    });
  };

  const handleGroupLabels = async (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    if (history.isApplying() || measurementIds.length < 2) return false;
    return enqueueHistoryEdit(async () => { try {
      const result = await groupMeasurementLabels({ measurementIds });
      recordGrouping(result);
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to group labels"));
      return false;
    } });
  };

  const handleUngroupLabels = async (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    if (history.isApplying() || measurementIds.length === 0) return false;
    return enqueueHistoryEdit(async () => { try {
      const result = await ungroupMeasurementLabels({ measurementIds });
      recordGrouping(result);
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to ungroup labels"));
      return false;
    } });
  };

  const handleGroupAllLabelsInScope = async () => {
    if (!planDocumentId || history.isApplying()) {
      return false;
    }
    const selected = pageMeasurements.filter((row) =>
      selectedMeasurementIds.includes(row._id)
    );
    const tagIds = new Set(selected.map((row) => (row.tagId ? String(row.tagId) : "")));
    if (tagIds.size !== 1) {
      toast.error("Select measurements from a single tag to group all labels");
      return false;
    }
    const tagId = selected[0]?.tagId;
    return enqueueHistoryEdit(async () => { try {
      const result = await groupAllMeasurementLabelsForTag({
        jobId,
        tagId: tagId ?? undefined,
        planDocumentId,
        pageNumber: activePage,
      });
      recordGrouping(result);
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to group all labels"));
      return false;
    } });
  };

  const handleUngroupAllLabelsInScope = async () => {
    if (!planDocumentId || history.isApplying()) {
      return false;
    }
    const selected = pageMeasurements.filter((row) =>
      selectedMeasurementIds.includes(row._id)
    );
    const tagIds = new Set(selected.map((row) => (row.tagId ? String(row.tagId) : "")));
    if (tagIds.size !== 1) {
      toast.error("Select measurements from a single tag to ungroup all labels");
      return false;
    }
    const tagId = selected[0]?.tagId;
    return enqueueHistoryEdit(async () => { try {
      const result = await ungroupAllMeasurementLabelsForTag({
        jobId,
        tagId: tagId ?? undefined,
        planDocumentId,
        pageNumber: activePage,
      });
      recordGrouping(result);
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to ungroup all labels"));
      return false;
    } });
  };

  return {
    handleGroupLabels,
    handleUngroupLabels,
    handleGroupAllLabelsInScope,
    handleUngroupAllLabelsInScope,
  };
}
